import { ChangeEvent } from 'react'
import { FieldErrors, UseFormRegister } from 'react-hook-form'

import FormGroup from '../../../core/components/FormGroup'
import { FormUploadPreview } from '../../../core/components/FormUploadPreview'
import { SeoPages } from '../../../common/models'
import { Button, Input } from '@/components/ui'
import { AlertRatio } from '@/components/AlertRatio'
import {
  ISEOValues,
  SeoFieldConfig,
  SeoImageFieldName,
  SEO_FIELDS,
  SEO_IMAGE_FIELD_NAMES,
} from './seo.config'

interface SeoImageState {
  name: string
  url: string
}

interface Props {
  index: number
  page: SeoPages
  register: UseFormRegister<ISEOValues>
  errors: FieldErrors<ISEOValues>
  hasEditPermission: boolean
  isUploading: boolean
  isErrorRatio: boolean
  images: Record<string, SeoImageState>
  invalidPhoto: Record<string, boolean>
  onImageChange: (field: SeoImageFieldName, index: number, file: File) => void
  onImageRemove: (field: SeoImageFieldName, index: number) => void
  onCancel: (index: number) => void
  onSubmit: React.FormEventHandler<HTMLFormElement>
  isSubmitting?: boolean
}

export const SeoPageForm: React.FC<Props> = ({
  index,
  page,
  register,
  errors,
  hasEditPermission,
  isUploading,
  isErrorRatio,
  images,
  invalidPhoto,
  onImageChange,
  onImageRemove,
  onCancel,
  onSubmit,
  isSubmitting,
}) => {
  const renderInput = (field: SeoFieldConfig) => {
    const error = errors?.content?.[index]?.[field.name]?.message
    return (
      <FormGroup className="flex flex-col gap-2 w-full" label={field.label}>
        <Input
          isInvalid={!!error}
          {...register(
            `content.${index}.${field.name}`,
            field.type === 'number' ? { valueAsNumber: true } : undefined,
          )}
          type={field.type ?? 'text'}
          placeholder={field.placeholder}
          disabled={!hasEditPermission}
        />
      </FormGroup>
    )
  }

  const renderImageField = (field: SeoFieldConfig) => {
    const name = field.name as SeoImageFieldName
    const key = `${index}.${name}`
    const image = images[key]
    const error = errors?.content?.[index]?.[name]?.message
    return (
      <FormUploadPreview
        control={
          <Input
            multiple={false}
            accept="image/*"
            disabled={!hasEditPermission}
            isInvalid={!!error || invalidPhoto[key]}
            name={name}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const file = e?.target?.files?.[0]
              if (file) onImageChange(name, index, file)
            }}
            type="file"
          />
        }
        isUploading={isUploading}
        imageName={image?.name}
        url={image?.url}
        onRemove={() => onImageRemove(name, index)}
        imageStyle={{ objectFit: 'contain' }}
        label={field.label}
        previewLabel={field.label}
      />
    )
  }

  const hasInvalidPhoto = SEO_IMAGE_FIELD_NAMES.some(
    (name) => invalidPhoto[`${index}.${name}`],
  )

  return (
    <form id={`settings-seo-form-${page}`} onSubmit={onSubmit}>
      <div className="grid grid-cols-2 gap-4 w-full">
        {SEO_FIELDS.map((field) => (
          <div
            key={field.name}
            className={
              field.kind === 'image' || field.fullWidth ? 'col-span-2' : ''
            }
          >
            {field.kind === 'image'
              ? renderImageField(field)
              : renderInput(field)}
          </div>
        ))}
      </div>

      {isErrorRatio && hasInvalidPhoto && <AlertRatio />}

      <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-monochrome-120 [&>*]:w-[154px]">
        <Button
          type="button"
          onClick={() => onCancel(index)}
          variant="secondary"
          data-dismiss="modal"
          disabled={!hasEditPermission}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          form={`settings-seo-form-${page}`}
          type="submit"
          disabled={isSubmitting || !hasEditPermission}
          loading={isSubmitting}
        >
          Save
        </Button>
      </div>
    </form>
  )
}
